/**
 * 桥梁管理 - 区域管理控制逻辑
 */
import { getAreasByBridgeId, createArea, updateArea, deleteArea } from '@/api/area';
import { getDictsByType } from '@/api/dict';
import BridgeEventBus, { BRIDGE_EVENTS } from '@/utils/BridgeEventBus';
import bridgeMapStore from '@/store/bridgeMapStore';
import mapUtils from '@/utils/mapUtils';
import L from 'leaflet';

// 生成随机编码函数
function generateRandomCode(prefix = '') {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  return `${prefix}_${timestamp}_${random}`;
}

export default {
  // 创建时的钩子函数，用于注册事件监听
  created() {
    // 监听状态重置事件
    BridgeEventBus.$on(BRIDGE_EVENTS.STATE_RESET, this.handleStateReset);
  },
  
  // 销毁前的钩子函数，用于移除事件监听
  beforeDestroy() {
    // 移除状态重置事件监听
    BridgeEventBus.$off(BRIDGE_EVENTS.STATE_RESET, this.handleStateReset);
  },
  
  computed: {
    // 从store获取绘制坐标数组
    drawingCoordinates: {
      get() {
        return bridgeMapStore.state.drawingCoordinates;
      },
      set(value) {
        // 由于这是数组，直接赋值会丢失响应式，使用简单的替换操作
        bridgeMapStore.state.drawingCoordinates = [...value];
      }
    },
    
    // 从store获取绘制状态
    isDrawingActive: {
      get() {
        return bridgeMapStore.state.isDrawing;
      },
      set(value) {
        bridgeMapStore.actions.startDrawing(value);
      }
    }
  },
  
  data() {
    return {
      // 区域管理相关
      isEditingArea: false,
      // isDrawingActive 移至计算属性
      // drawingCoordinates 移至计算属性，从bridgeMapStore获取
      savedAreas: [],
      areaForm: {
        id: null,
        areaName: '',
        areaCode: '',
        areaType: '',
        bridgeId: null,
        coordinates: '',
        description: '',
        direction: 0,
        isEnabled: 1
      },
      // 区域类型和名称映射
      areaTypeNameMap: {},
      areaRules: {
        areaName: [
          { required: true, message: '请输入区域名称', trigger: 'blur' },
          { max: 50, message: '长度不能超过50个字符', trigger: 'blur' }
        ],
        areaType: [
          { required: true, message: '请选择区域类型', trigger: 'change' }
        ]
      },
      // 区域类型选项（将从数据字典加载）
      areaTypeOptions: []
    };
  },
  
  methods: {
    // 处理状态重置事件
    handleStateReset() {
      console.log('区域控制器响应状态重置事件...');
      
      // 重置本地状态
      this.isEditingArea = false;
      this.selectedArea = null;
      this.savedAreas = [];
      
      // 重置区域表单
      this.areaForm = {
        id: null,
        areaName: '',
        areaCode: '',
        areaType: '',
        bridgeId: null,
        coordinates: '',
        description: '',
        direction: 0,
        isEnabled: 1
      };
      
      console.log('区域控制器状态已重置');
    },
    
    // 加载区域类型选项
    async loadAreaTypeOptions() {
      try {
        console.log('开始加载区域类型选项...');
        // 尝试从数据字典加载区域类型选项
        const response = await getDictsByType('AREA_TYPE');
        
        // 可用的有效枚举值列表 (来自后端AreaTypeEnum)
        const validEnums = [
          'BRIDGE', 'BRIDGE_LINE', 'BRIDGE_MAST', 'CHANNEL', 
          'CHANNEL_DOWN', 'CHANNEL_UP', 'NAVIGATION', 'NAVIGATION_DOWN', 
          'NAVIGATION_UP', 'NO_NAVIGATION', 'OTHER', 'RADAR_AREA_DOWN', 
          'RADAR_AREA_UP', 'REPORT_LINE_DOWN', 'REPORT_LINE_UP', 
          'RESTRICTED', 'WARNING', 'WARNING_LINE_DOWN', 'WARNING_LINE_UP'
        ];
        
        console.log('API响应:', JSON.stringify(response).substring(0, 300) + '...');
        
        if (response && response.code === 200 && Array.isArray(response.data)) {
          // 确保dictValue是枚举值
          const areaTypeOptions = [];
          const areaTypeNameMap = {};
          
          response.data.forEach(item => {
            // 使用dictCode作为枚举值，dictValue作为显示名称
            if (item.dictCode && validEnums.includes(item.dictCode)) {
              const enumValue = item.dictCode; // 枚举值
              const displayName = item.dictValue; // 中文显示名称
              
              // 构建选项
              areaTypeOptions.push({
                value: enumValue, // 使用枚举值
                label: displayName // 使用中文显示名称
              });
              
              // 构建名称映射
              areaTypeNameMap[enumValue] = displayName;
            } else {
              console.warn(`字典项 ${item.dictValue || item.dictCode || 'unknown'} 没有有效枚举值，跳过`);
            }
          });
          
          this.areaTypeOptions = areaTypeOptions;
          this.areaTypeNameMap = areaTypeNameMap;
          
          console.log('处理后的区域类型选项:', 
            areaTypeOptions.map(o => `${o.label}:${o.value}`).join(', '));
          console.log('区域类型映射:', JSON.stringify(this.areaTypeNameMap));
          
          // 如果没有设置当前区域类型且有选项可用，设置默认值
          if (this.areaForm && this.areaTypeOptions.length > 0) {
            if (!this.areaForm.areaType) {
              this.areaForm.areaType = this.areaTypeOptions[0].value;
            }
          }
          
          console.log('区域类型加载成功，数量:', this.areaTypeOptions.length);
        } else {
          console.warn('加载区域类型失败或无数据:', response);
          // 提供默认的区域类型选项，使用有效枚举值
          this.areaTypeOptions = [
            // { value: 'RESTRICTED', label: '限制区' },
            { value: 'WARNING', label: '警戒区' },
            // { value: 'CHANNEL', label: '航道' },
            { value: 'BRIDGE', label: '桥梁区' },
            { value: 'BRIDGE_MAST', label: '桥墩' },
            { value: 'OTHER', label: '其他' }
          ];
          
          console.log('使用默认区域类型选项:', JSON.stringify(this.areaTypeOptions));
          
          // 创建默认映射
          this.areaTypeNameMap = {};
          this.areaTypeOptions.forEach(item => {
            this.areaTypeNameMap[item.value] = item.label;
          });
        }
      } catch (error) {
        console.error('加载区域类型选项失败:', error);
        // 提供默认的区域类型选项，使用有效枚举值
        this.areaTypeOptions = [
          // { value: 'RESTRICTED', label: '限制区' },
          { value: 'WARNING', label: '警戒区' },
          // { value: 'CHANNEL', label: '航道' },
          { value: 'BRIDGE', label: '桥梁区' },
          { value: 'BRIDGE_MAST', label: '桥墩' },
          { value: 'OTHER', label: '其他' }
        ];
        
        console.log('出错后使用默认区域类型选项:', JSON.stringify(this.areaTypeOptions));
        
        // 创建默认映射
        this.areaTypeNameMap = {};
        this.areaTypeOptions.forEach(item => {
          this.areaTypeNameMap[item.value] = item.label;
        });
        
        // 设置默认区域类型（添加防御性检查）
        if (this.areaForm && this.areaTypeOptions.length > 0) {
          if (!this.areaForm.areaType) {
            this.areaForm.areaType = this.areaTypeOptions[0].value;
            console.log('设置默认区域类型:', this.areaForm.areaType);
          }
        }
      }
    },
    
    // 获取区域类型名称
    getAreaTypeName(areaType) {
      return this.areaTypeNameMap[areaType] || areaType || '未知类型';
    },
    
    // 获取区域显示颜色
    getAreaColor(areaType) {
      // 根据区域类型返回不同颜色
      const colorMap = {
        'AREA_NORMAL': '#42b983', // 普通区域 - 绿色
        'AREA_WARNING': '#e6a23c', // 警示区域 - 黄色
        'AREA_RESTRICTED': '#f56c6c', // 限制区域 - 红色
        'LINE_NORMAL': '#409eff', // 普通线路 - 蓝色
        'LINE_WARNING': '#e6a23c' // 警示线路 - 黄色
      };
      
      return colorMap[areaType] || '#42b983'; // 默认绿色
    },
    
    // 加载区域数据
    async loadBridgeAreas(bridgeId) {
      if (!bridgeId) {
        console.warn('尝试加载区域数据时未提供bridgeId');
        this.savedAreas = [];
        return;
      }
      
      try {
        console.log('开始重新加载区域数据，桥梁ID:', bridgeId);
        this.loading = true;
        
        // 尝试加载区域数据，最多重试2次
        let response = null;
        let retryCount = 0;
        const maxRetries = 2;
        
        while (retryCount <= maxRetries && (!response || response.code !== 200)) {
          try {
            response = await getAreasByBridgeId(bridgeId);
            if (response && response.code === 200) break;
            
            // 如果请求成功但状态码不是200，不再重试
            if (response) break;
            
            retryCount++;
            if (retryCount <= maxRetries) {
              console.log(`区域数据加载失败，尝试重试(${retryCount}/${maxRetries})...`);
              await new Promise(resolve => setTimeout(resolve, 1000)); // 等待1秒再重试
            }
          } catch (retryError) {
            console.error('加载区域数据出错，尝试重试:', retryError);
            retryCount++;
            if (retryCount > maxRetries) throw retryError;
            await new Promise(resolve => setTimeout(resolve, 1000)); // 等待1秒再重试
          }
        }
        
        if (response && response.code === 200 && Array.isArray(response.data)) {
          console.log('区域数据加载成功，数量:', response.data.length);
          
          // 跟踪过滤过程中的错误，以便后续聚合输出
          let noCoordinatesCount = 0;
          let insufficientPointsCount = 0;
          let parseErrorCount = 0;
          
          // 统计不同几何类型的区域数量
          let pointCount = 0;
          let polygonCount = 0;
          let lineCount = 0;
          let otherCount = 0;
          
          // 验证和过滤区域数据，确保每个区域都有有效的坐标数据
          const validAreas = response.data.filter(area => {
            if (!area || !area.coordinates) {
              noCoordinatesCount++;
              return false;
            }
            
            // 统计几何类型
            switch (area.geometryType) {
              case 'POINT':
                pointCount++;
                break;
              case 'LINESTRING':
              case 'LINE':
                lineCount++;
                break;
              case 'POLYGON':
              case 'RECTANGLE':
              case 'CIRCLE':
                polygonCount++;
                break;
              default:
                otherCount++;
            }
            
            // 尝试验证坐标格式
            try {
              const coords = mapUtils.parseAreaCoordinates(area);
              
              // 根据几何类型进行不同的验证
              let isValid = false;
              if (area.geometryType === 'POINT') {
                // 点标注只需要1个有效坐标点
                isValid = coords && coords.length >= 1;
              } else {
                // 线段、多边形等需要至少2个点
                isValid = coords && coords.length >= 2;
              }
              
              if (!isValid) {
                insufficientPointsCount++;
                return false;
              }
              return true;
            } catch (error) {
              parseErrorCount++;
              return false;
            }
          });
          
          // 聚合错误统计和类型统计
          if (noCoordinatesCount > 0 || insufficientPointsCount > 0 || parseErrorCount > 0) {
            console.warn(`区域数据过滤结果：${validAreas.length}/${response.data.length} 个区域有效，` +
                          `${noCoordinatesCount} 个无坐标，` +
                          `${insufficientPointsCount} 个点数不足，` +
                          `${parseErrorCount} 个解析错误`);
            console.log(`几何类型分布：点标注${pointCount}个，多边形${polygonCount}个，线段${lineCount}个，其他${otherCount}个`);
          } else {
            console.log(`所有区域数据有效，数量: ${validAreas.length}`);
            console.log(`几何类型分布：点标注${pointCount}个，多边形${polygonCount}个，线段${lineCount}个，其他${otherCount}个`);
          }
          
          // 同步到状态存储
          bridgeMapStore.actions.setAreas(validAreas);
          this.savedAreas = [...validAreas];
          
          // 如果当前正在编辑的区域在新加载的数据中不存在，重置编辑状态
          if (this.isEditingArea && this.areaForm.id) {
            const stillExists = this.savedAreas.some(area => area.id === this.areaForm.id);
            if (!stillExists) {
              this.isEditingArea = false;
              this.drawingCoordinates = [];
            }
          }
          
          // 通知区域数据已加载 - 使用throttleEmit方法防止频繁触发
          try {
            if (BRIDGE_EVENTS && BRIDGE_EVENTS.AREA_DATA_LOADED) {
              // 使用新的节流方法发送事件，自动处理节流逻辑
              const sent = BridgeEventBus.throttleEmit(BRIDGE_EVENTS.AREA_DATA_LOADED, this.savedAreas, 300);
              if (sent) {
                console.log('发送区域数据加载完成事件');
              }
            } else {
              console.warn('无法发送区域数据加载事件: 事件常量未定义');
            }
          } catch (error) {
            console.warn('发送区域数据加载事件失败，但不影响功能:', error);
          }
          
          // 刷新地图以显示新加载的区域
          if (this.map) {
            setTimeout(() => {
              try {
                this.map.invalidateSize();
              } catch (e) {
                console.warn('地图刷新失败，但不影响使用', e);
              }
            }, 200);
          }
        } else {
          console.error('加载区域数据失败:', response?.message || '未知错误');
          this.$message.error('加载区域数据失败');
          this.savedAreas = [];
        }
      } catch (error) {
        console.error('加载区域数据失败:', error);
        this.$message.error('加载区域数据失败，请检查网络连接');
        this.savedAreas = [];
      } finally {
        this.loading = false;
      }
    },
    
    // 开始绘制区域 - 使用统一store方法
    startDrawing(active) {
      // 重置选中的区域
      if (active === true) {
        console.log('启动绘制前，重置选中区域');
        this.selectedArea = null;
      }
      
      // 调用store中的方法，获取状态
      const isActive = bridgeMapStore.actions.startDrawing(active);
      
      // 不再需要同步isDrawingActive，因为它现在是计算属性
      
      // 如果开始绘制模式，显示提示
      if (isActive) {
        // 设置编辑状态，确保后续能够保存区域
        this.isEditingArea = true;
        this.$message.info('区域绘制模式已开启，点击地图添加点，绘制完成后点击"保存"按钮');
        
        // 输出当前状态用于调试
        console.log('开始绘制，当前坐标点数:', this.drawingCoordinates.length);
      } else if (!isActive && this.drawingCoordinates.length > 0) {
        // 从绘制模式退出但有内容，提示保存
        this.$message.info('绘制模式已关闭，请记得保存您的绘制内容');
      }
      
      // 返回当前状态
      return isActive;
    },
    
    // 清除绘制内容 - 使用统一store方法
    clearDrawing() {
      // 先备份坐标数组长度，用于判断是否真的需要刷新地图
      const hadCoordinates = this.drawingCoordinates.length > 0;
      
      // 使用store方法清空绘制内容
      bridgeMapStore.actions.clearDrawing();
      
      // 重置编辑状态
      this.isEditingArea = false;
      
      // 如果之前有坐标，且地图实例存在，则刷新地图
      if (hadCoordinates && this.map) {
        setTimeout(() => {
          try {
            this.map.invalidateSize({animate: false});
          } catch (e) {
            console.warn('地图尺寸更新失败，但不影响使用', e);
          }
        }, 100);
      }
    },
    
    /**
     * 保存区域
     * @param {Object} areaData 可选的区域数据，默认使用本地表单数据
     * @returns {Promise<boolean>} 是否保存成功
     */
    async saveArea(areaData) {
      if (this.saving) {
        this.$message.warning('正在保存，请稍候...');
        return false;
      }

      try {
        this.saving = true;
        
        // 使用传入的areaData或默认使用本地表单数据
        let formData = areaData || { ...this.areaForm };
        
        // 确保基本字段存在
        if (!formData.bridgeId && this.bridgeForm && this.bridgeForm.id) {
          formData.bridgeId = this.bridgeForm.id;
        }
        
        // 确保areaName字段存在（接口需要）
        if (!formData.areaName && formData.name) {
          formData.areaName = formData.name;
        } else if (!formData.name && formData.areaName) {
          formData.name = formData.areaName;
        }
        
        // 如果没有区域名称，生成一个默认名称
        if (!formData.areaName) {
          formData.areaName = `区域_${new Date().getTime()}`;
          formData.name = formData.areaName;
        }

        // 区域名称重名校验（问题#7兜底：同一桥梁下不允许重名区域，排除自身）
        const newAreaName = String(formData.areaName || formData.name || '').trim();
        const editingAreaId = formData.id;
        const duplicateArea = (this.savedAreas || []).find(a =>
          a.id !== editingAreaId &&
          String(a.areaName || a.name || '').trim() === newAreaName
        );
        if (duplicateArea) {
          this.$message.warning(`同一桥梁下已存在同名区域「${newAreaName}」，请更换名称`);
          this.saving = false;
          return false;
        }
        
        // 确保区域类型已设置
        if (!formData.areaType && this.areaTypeOptions.length > 0) {
          // 使用第一个可用的区域类型
          formData.areaType = this.areaTypeOptions[0].value;
        }
        
        // 处理坐标数据
        // 如果有传入的坐标数组，优先使用
        if (this.drawingCoordinates && this.drawingCoordinates.length >= 1) {
          // 如果是点标注类型，特殊处理
          if (formData.geometryType === 'POINT') {
            // 点标注至少需要一个点
            if (this.drawingCoordinates.length >= 1) {
              // 对于点标注，直接使用坐标数组（标准GeoJSON格式）
              formData.coordinates = JSON.stringify(this.drawingCoordinates[0]); // 使用第一个点的坐标
              
              // 确保图标字段有默认值
              if (!formData.icon) {
                formData.icon = 'el-icon-location';
              }
              
              console.log('保存点标注数据 - coordinates:', formData.coordinates, 'icon:', formData.icon);
            }
          } else {
            // 非点标注类型(如多边形、线段、矩形、圆形等)，需要至少2个点
            if (this.drawingCoordinates.length >= 2) {
              // 使用工具方法格式化坐标为GeoJSON
              const isLine = formData.areaType && formData.areaType.includes('LINE');
              const geometryType = formData.geometryType || 'POLYGON';
              formData.coordinates = mapUtils.formatCoordinatesToGeoJson(this.drawingCoordinates, isLine, geometryType);
            } else {
              this.$message.warning('线段、多边形、矩形和圆形区域至少需要2个点');
              this.saving = false;
              return false;
            }
          }
        } else {
          // 检查是否有传入的pointCoordinates (适用于点标注)
          if (formData.geometryType === 'POINT' && formData.pointCoordinates) {
            // 对于点标注，直接使用坐标数组（标准GeoJSON格式）
            formData.coordinates = JSON.stringify(formData.pointCoordinates);
            
            // 确保图标字段正确设置
                      if (!formData.icon) {
            formData.icon = 'el-icon-location';
          }
          
          console.log('使用pointCoordinates保存点标注数据 - coordinates:', formData.coordinates, 'icon:', formData.icon);
          }
        }
        
        // 确保坐标字段存在
        if (!formData.coordinates) {
          this.$message.warning('区域坐标不能为空，请先绘制区域');
          this.saving = false;
          return false;
        }
        
        console.log('保存区域数据:', JSON.stringify(formData));
        
        // 保存区域数据到后端
        let response = null;
        let savedSuccessfully = false;
        
        // 注释方案1 - 暂时只使用方案2
        /*
        // 尝试两种不同的方式保存区域数据
        // 方案1：使用完整的GeoJSON格式
        try {
          console.log('尝试方案1 - 使用GeoJSON格式');
          
          // 根据是否有ID决定是更新还是创建
          if (formData.id) {
            console.log('尝试更新区域 (方案1) - 使用GeoJSON格式');
            response = await updateArea(formData.id, formData);
            if (response && response.code === 200) savedSuccessfully = true;
          } else {
            console.log('尝试创建区域 (方案1) - 使用GeoJSON格式');
            response = await createArea(formData);
            if (response && response.code === 200) savedSuccessfully = true;
          }
        } catch (err1) {
          console.log('方案1失败:', err1);
        }
        */
        
        // 直接使用方案2，不再判断方案1是否成功
        // if (!savedSuccessfully) {
          try {
            console.log('使用简化数据格式保存区域');
            
            // 创建一个简化的数据对象，去掉可能导致问题的复杂对象
            const simpleData = {
              id: formData.id,
              bridgeId: formData.bridgeId,
              areaName: formData.areaName || formData.name,
              name: formData.name || formData.areaName, // 确保name字段也同步
              areaCode: formData.areaCode || generateRandomCode('AREA'),
              areaType: formData.areaType,
              coordinates: formData.coordinates,
              description: formData.description || '',
              lineDirection: formData.lineDirection || 0,
              isEnabled: formData.isEnabled !== undefined ? formData.isEnabled : 1,
              color: formData.color || '#3388ff', // 添加color字段
              geometryType: formData.geometryType || 'POLYGON', // 添加geometryType字段
              icon: formData.icon, // 添加icon字段（仅对点标注有效）
            };
            
            if (simpleData.id) {
              console.log('更新区域');
              response = await updateArea(formData.id, simpleData);
            } else {
              console.log('创建区域');
              response = await createArea(simpleData);
            }
          } catch (err2) {
            console.log('保存区域失败:', err2);
            throw err2; // 重新抛出异常，让外层catch处理
          }
        // }
        
        // 无论使用哪种方案，只要有一个成功就处理响应
        if (response) {
          if (response.code === 200) {
            this.$message.success('保存区域成功');
            
            // 清空当前的绘制坐标
            bridgeMapStore.actions.clearDrawing();
            
            // 重置编辑状态
            this.isEditingArea = false;
            
            // 添加新保存的区域到列表中
            if (response.data) {
              const savedArea = response.data;
              
              // 查找是否已存在相同ID的区域
              const existingIndex = this.savedAreas.findIndex(area => area.id === savedArea.id);
              
              if (existingIndex >= 0) {
                // 更新已存在的区域
                this.savedAreas.splice(existingIndex, 1, savedArea);
              } else {
                // 添加新区域
                this.savedAreas.push(savedArea);
              }
              
              // 更新选中的区域为新保存的区域
              this.selectedArea = savedArea;
            }
            
            // 触发区域保存成功事件
            BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_UPDATED, formData);
            
            // 重新加载区域列表，确保展示最新数据
            if (formData.bridgeId) {
              this.loadBridgeAreas(formData.bridgeId);
            }
            
            return true;
          } else {
            this.$message.error(`保存区域失败: ${response.message || '未知错误'}`);
            console.error('保存区域失败:', response);
            return false;
          }
        } else {
          this.$message.error('保存区域失败');
          console.error('保存区域失败');
          return false;
        }
      } catch (error) {
        this.$message.error(`保存区域时出错: ${error.message || '未知错误'}`);
        console.error('保存区域时出错:', error);
        return false;
      } finally {
        this.saving = false;
      }
    },
    
    // 编辑区域
    editArea(area) {
      try {
        // 处理特殊情况：isNew标记，创建新区域
        if (area && area.isNew === true) {
          console.log('创建新区域并进入编辑模式');
          
          // 切换到区域编辑标签页
          this.activeTab = 'area';
          
          // 设置编辑状态
          this.isEditingArea = true;
          
          // 从AreaPanel的localAreaForm中获取数据，如果可用
          if (this.$refs.areaPanel && this.$refs.areaPanel.localAreaForm) {
            this.areaForm = JSON.parse(JSON.stringify(this.$refs.areaPanel.localAreaForm));
          } else {
            // 否则使用默认值
            this.areaForm = {
              id: null,
              bridgeId: this.bridgeForm ? this.bridgeForm.id : null,
              name: '新区域',
              areaName: '新区域',
              areaCode: '',
              areaType: this.areaTypeOptions.length > 0 ? this.areaTypeOptions[0].value : '',
              geometryType: 'POLYGON',
              description: '',
              coordinates: '',
              isEnabled: 1,
              color: '#3388ff' // 添加默认颜色
            };
          }
          
          return; // 创建新区域后直接返回
        }
        
        if (!area) {
          console.warn('尝试编辑无效的区域数据');
          return;
        }
      
        this.isEditingArea = true;
        
        // 深拷贝区域数据，避免引用问题
        this.areaForm = JSON.parse(JSON.stringify(area));
        
        // 确保设置当前桥梁ID
        if (this.bridgeForm && this.bridgeForm.id) {
          this.currentBridgeId = this.bridgeForm.id;
          this.areaForm.bridgeId = this.bridgeForm.id;
          console.log('编辑区域时设置桥梁ID:', this.currentBridgeId);
        } else if (area.bridgeId) {
          this.currentBridgeId = area.bridgeId;
          console.log('从区域数据中获取桥梁ID:', this.currentBridgeId);
        } else {
          console.warn('编辑区域时无法确定桥梁ID');
        }
        
        // 兼容不同的字段命名
        if (!this.areaForm.name && this.areaForm.areaName) {
          this.areaForm.name = this.areaForm.areaName;
        }
        if (!this.areaForm.areaName && this.areaForm.name) {
          this.areaForm.areaName = this.areaForm.name;
        }
        
        // 确保color字段存在
        if (!this.areaForm.color) {
          this.areaForm.color = '#3388ff'; // 默认使用蓝色
          console.log('设置默认区域颜色:', this.areaForm.color);
        }
      
        // 同步到状态存储，设置skipEvent=true避免触发事件
        bridgeMapStore.actions.setCurrentArea(area, true);
      
        // 解析坐标数据并设置
        console.log('开始解析区域坐标:', area.id, area.name || area.areaName);
        
        const coordinates = mapUtils.parseAreaCoordinates(area);
        if (coordinates && coordinates.length > 0) {
          this.drawingCoordinates = coordinates;
          console.log('区域坐标解析成功，点数:', coordinates.length);
          
          // 切换到区域编辑标签页
          this.activeTab = 'area';
        } else {
          this.drawingCoordinates = [];
          console.warn('区域坐标解析失败或为空', area.coordinates);
          this.$message.warning('区域坐标解析失败，无法编辑图形，但可以重新绘制');
        }
      } catch (error) {
        console.error('处理区域坐标失败:', error);
        this.drawingCoordinates = [];
        this.$message.error('处理区域坐标失败，请重试');
      }
    },
    
    // 删除区域 - 已由AreaPanel组件处理确认对话框，这里直接执行删除
    async deleteArea(area) {
      if (!area || !area.id) {
        this.$message.warning('无效的区域数据');
        return;
      }
      
      try {
        this.submitting = true;
        
        const response = await deleteArea(area.id);
        
        if (response && response.code === 200) {
          this.$message.success('删除区域成功');
          
          // 重新加载区域数据
          await this.loadBridgeAreas(this.bridgeForm.id);
          
          // 如果正在编辑被删除的区域，重置表单
          if (this.areaForm.id === area.id) {
            this.resetAreaForm();
          }
        } else {
          this.$message.error(response?.message || '删除区域失败');
        }
      } catch (error) {
        console.error('删除区域失败:', error);
        this.$message.error('删除区域失败，请检查网络连接');
      } finally {
        this.submitting = false;
      }
    },
    
    // 重置区域表单
    resetAreaForm() {
      this.isEditingArea = false;
      // 使用store方法关闭绘制模式
      bridgeMapStore.actions.startDrawing(false);
      // 使用store方法清空绘制坐标
      bridgeMapStore.actions.clearDrawing();
      
      // 获取当前桥梁ID，优先使用bridgeForm.id，其次使用currentBridgeId
      const currentBridgeId = (this.bridgeForm && this.bridgeForm.id) || this.currentBridgeId;
      
      if (!currentBridgeId) {
        console.warn('重置区域表单时无法确定桥梁ID');
      } else {
        console.log('重置区域表单时设置桥梁ID:', currentBridgeId);
      }
      
      this.areaForm = {
        id: null,
        bridgeId: currentBridgeId,
        name: '',
        areaName: '',
        // 确保使用选项中的值（枚举值），而不是标签文本
        areaType: this.areaTypeOptions.length > 0 ? this.areaTypeOptions[0].value : '',
        description: '',
        coordinates: ''
      };
    },
    
    // 切换区域类型
    changeAreaType(areaType) {
      if (this.areaForm) {
        if(areaType == 'POINT'){
          areaType = 'OTHER';
          console.log('检测到区域类型(areaType)错误设置为POINT，自动纠正为OTHER（POINT是几何类型而非区域类型）');
        }
        this.areaForm.areaType = areaType;
        // console.log('【后】changeAreaType切换区域类型为POINT');
      }
    },
    
    // 取消编辑区域
    cancelEditArea() {
      // 如果有未保存的绘制内容，提示用户
      if (this.drawingCoordinates.length > 2) {
        this.$confirm('有未保存的区域绘制内容，确定要取消吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '继续编辑',
          type: 'warning'
        }).then(() => {
          this.resetAreaForm();
        }).catch(() => {
          // 用户选择继续编辑，不执行任何操作
        });
      } else {
        // 没有实质内容，直接取消
        this.resetAreaForm();
      }
    },
    
    // 选择区域（缩放到指定区域）
    selectArea(area) {
      if (this.map && area) {
        try {
          // 确保绘制模式已关闭
          if (bridgeMapStore.state.isDrawing) {
            console.log('选择区域时关闭绘制模式');
            bridgeMapStore.actions.startDrawing(false);
          }
          
          // 确保设置当前桥梁ID
          if (this.bridgeForm && this.bridgeForm.id) {
            this.currentBridgeId = this.bridgeForm.id;
            console.log('选择区域时设置桥梁ID:', this.currentBridgeId);
          } else if (area.bridgeId) {
            this.currentBridgeId = area.bridgeId;
            console.log('从区域数据中获取桥梁ID:', this.currentBridgeId);
          } else {
            console.warn('选择区域时无法确定桥梁ID');
          }
          
          // 使用mapUtils直接解析区域坐标
          const latLngs = mapUtils.parseAreaCoordinates(area);
          
          // 验证解析结果
          if (!latLngs || latLngs.length === 0) {
            console.warn('区域坐标解析失败或为空');
            return;
          }
          
          console.log('成功解析区域坐标，点数:', latLngs.length);
          
          // 使用Leaflet缩放到该区域
          const bounds = L.latLngBounds(latLngs);
          
          // 确保边界有效
          if (bounds.isValid()) {
            this.map.fitBounds(bounds);
          } else {
            console.warn('无法创建有效的坐标边界');
          }
        } catch (error) {
          console.error('解析区域坐标或缩放到区域失败:', error);
        }
      }
    },

    // 修改createAreaFromDrawing方法
    createAreaFromDrawing() {
      // 检查是否有有效的绘制坐标
      if (this.drawingCoordinates.length < 2) {
        this.$message.warning('请至少绘制2个点形成有效线段或区域');
        return;
      }
      
      // 重置表单
      this.resetAreaForm();
      
      // 设置当前桥梁ID
      if (this.bridgeForm && this.bridgeForm.id) {
        this.areaForm.bridgeId = this.bridgeForm.id;
      }
      
      // 判断是否是线段 (只有2个点)
      const isLineSegment = this.drawingCoordinates.length === 2;
      
      // 如果是线段，尝试自动选择线段类型的选项
      if (isLineSegment) {
        // 寻找第一个包含LINE的区域类型
        const lineTypeOption = this.areaTypeOptions.find(option => option.value.includes('LINE'));
        
        if (lineTypeOption) {
          this.areaForm.areaType = lineTypeOption.value;
        } else {
          // 如果没找到合适的线段类型，使用默认值
          this.areaForm.areaType = this.areaTypeOptions.length > 0 ? 
            this.areaTypeOptions[0].value : 'WARNING';
        }
      } else {
        // 如果是多边形，选择非线段类型
        const areaTypeOption = this.areaTypeOptions.find(option => !option.value.includes('LINE'));
        
        if (areaTypeOption) {
          this.areaForm.areaType = areaTypeOption.value;
        } else {
          // 使用默认值
          this.areaForm.areaType = this.areaTypeOptions.length > 0 ? 
            this.areaTypeOptions[0].value : 'WARNING';
        }
      }
      
      // 设置区域名称为选中的区域类型名称
      if (this.areaForm.areaType && this.areaTypeNameMap[this.areaForm.areaType]) {
        this.areaForm.name = this.areaTypeNameMap[this.areaForm.areaType];
      }
      
      // 设置为编辑状态
      this.isEditingArea = true;
    }
  }
}; 